/* eslint-disable @typescript-eslint/ban-types */
import React, { ChangeEvent, FocusEvent, useEffect, useRef, useState } from 'react';
import { renderToString } from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import {
  FormControl,
  InputAdornment,
  Select,
  OutlinedInput,
  Typography,
  MenuItem,
  IconButton,
  SelectChangeEvent,
} from '@mui/material';
import {
  DragIndicatorOutlined as DragIndicatorOutlinedIcon,
  CloseOutlined as CloseOutlinedIcon,
  CropOriginalOutlined as CropOriginalOutlinedIcon,
  AltRouteOutlined as AltRouteOutlinedIcon,
} from '@mui/icons-material';
import styled from 'styled-components';
import ReactHtmlParser from 'react-html-parser';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { PALETTE, BASE_FONT_FAMILY, BUTTON } from '~/assets/styles/base';
import { ORIGINAL_FORM_VALIDATION } from '~/common/constants';
import { getOriginalFormsValidationMsg } from '~/common/functional';
import {
  SingleAnswerConfigAnswerProps,
  SingleAnswerConfigProps,
  OriginalFormQuestionIdMap,
} from '~/common/types';
import InputValidationErrorTooltip from '~/components/ui-parts/InputValidationErrorTooltip';
import ImageUploadModal from '../../ImageUploadModal';
import { LOCALE_STRING } from '~/constants';

const ContentBox = styled.div``;

const AnswerComponent = styled.div`
  width: 716px;
  display: flex;
  flex-direction: row;
  padding: 16px 7px;
  height: auto;
  align-items: center;
`;

const AnswerInput = styled.div`
  width: 100%;
`;

const StyledMenuItem = styled(MenuItem)`
  font-size: 14px;
  color: ${PALETTE.text.main};
  font-family: ${BASE_FONT_FAMILY};
  margin-bottom: 10px;
  width: 100%;
`;

const DragIconWrapper = styled(DragIndicatorOutlinedIcon)`
  color: ${BUTTON.dragIcon.unactive};
  fontsize: 16px;
  &:hover {
    color: ${BUTTON.dragIcon.active};
    cursor: move;
  }
  &:focus {
    outline: none;
  }
`;

const ImgPreviewContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
  margin-bottom: 20px;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-start;
`;

const StyledImg = styled.img`
  max-height: 170px;
`;

const StyledDisabledTextDiv = styled.div`
  margin: -15px 5px -15px 10px;
`;

const StyledDisabledText = styled(Typography)`
  font-size: 14px;
  color: #425f77;
`;

type Props = {
  id: string;
  questionIdx: number;
  totalQuestions: number;
  isPullDown?: boolean;
  questionIds: Array<OriginalFormQuestionIdMap>;
  answerIdx: number;
  handleAnswerChange: Function;
  singleAnswerConfig: SingleAnswerConfigProps;
  isValidate?: boolean;
  item: SingleAnswerConfigAnswerProps;
  answersListComponents: Array<SingleAnswerConfigAnswerProps>;
  setAnswersListComponents: Function;
  handleFormValidationError: Function;
};

const SortableItem = ({
  id,
  questionIdx,
  totalQuestions,
  isPullDown,
  questionIds,
  answerIdx,
  handleAnswerChange,
  singleAnswerConfig,
  isValidate,
  item,
  answersListComponents,
  setAnswersListComponents,
  handleFormValidationError,
}: Props) => {
  const { t } = useTranslation();

  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const [imageUploadAnswerIdx, setImageUploadAnswerIdx] = useState(-1);

  const myRef = useRef<HTMLDivElement>(null);

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform)?.replace(/scaleY\([^)]*\)\s*/, ''),
    transition,
  };

  const getGotoOptions = (compoIdx: number) => {
    const options: JSX.Element[] = [];

    options.push(
      <StyledMenuItem value="" key={`${compoIdx}-default`}>
        <AltRouteOutlinedIcon sx={{ marginRight: '10px' }} />
        {t('registration.originalForm.manual.saComponent.optionPlaceHolder')}
      </StyledMenuItem>
    );

    questionIds.forEach((question) => {
      if (questionIdx + 1 < question.questionNum)
        options.push(
          <StyledMenuItem key={question.questionId} value={question.questionId}>
            <AltRouteOutlinedIcon sx={{ marginRight: '10px' }} />
            {`Q${question.questionNum}`}
          </StyledMenuItem>
        );
    });

    options.push(
      <StyledMenuItem key={`${compoIdx}-end`} value="END">
        <AltRouteOutlinedIcon sx={{ marginRight: '10px' }} />
        END
      </StyledMenuItem>
    );

    return options;
  };

  const showInputError = (tmpIsValidate?: boolean, tmpIsInputValid?: boolean) => {
    if (tmpIsValidate !== undefined && tmpIsValidate) {
      if (tmpIsInputValid !== undefined) {
        return !tmpIsInputValid;
      }
    }
    return false;
  };

  const isImgPreviewForAnswer = (answerLabel: string) => {
    const currentLabel = answerLabel;

    const imgTagRegex = /<img.*\/>/g;
    const newLabel = currentLabel.match(imgTagRegex);

    if (newLabel !== null) {
      return ReactHtmlParser(newLabel[0])[0];
    }

    return null;
  };

  const setImgUrlToLabel = (previewData: JSX.Element) => {
    const imgUrl = renderToString(previewData);

    const newAnswersListComponents = answersListComponents.map((answerItem, itemIdx) => {
      // if the item.label already contains img tag, replace it with new img tag
      if (itemIdx === imageUploadAnswerIdx) {
        if (answerItem.label.includes('<img')) {
          return {
            ...answerItem,
            label: answerItem.label.replace(/<img.*\/>/g, imgUrl),
          };
        }
        return {
          ...answerItem,
          label: answerItem.label + imgUrl,
        };
      }

      return answerItem;
    });

    setAnswersListComponents(newAnswersListComponents);
    handleAnswerChange(newAnswersListComponents);
    setImageUploadAnswerIdx(-1);
  };

  const handleImageUploadSetting = (value?: JSX.Element) => {
    setShowImageUploadModal(false);

    if (value) {
      setImgUrlToLabel(value);
    }
  };

  const handleOnChangeComponent = (
    event: ChangeEvent<HTMLInputElement> | FocusEvent<HTMLInputElement> | SelectChangeEvent<string>,
    compoIdx: number,
    type: string,
    onBlur: boolean,
    answerImgPreview?: JSX.Element
  ) => {
    const newAnswersListComponents = answersListComponents.map((answerItem, itemIdx) => {
      if (itemIdx === compoIdx) {
        if (type === 'label') {
          let answerImgData = '';
          if (answerImgPreview) {
            answerImgData = renderToString(answerImgPreview);
          }

          return {
            ...answerItem,
            isValid: true,
            label: event.target.value + answerImgData,
          };
        }
        return {
          ...answerItem,
          isValid: true,
          goto: event.target.value,
        };
      }

      return answerItem;
    });

    setAnswersListComponents(newAnswersListComponents);
    handleAnswerChange(newAnswersListComponents, { ...singleAnswerConfig }, onBlur);
  };

  const getAnswerLabel = (answerLabel: string) => {
    const currentLabel = answerLabel;

    const imgTagRegex = /<img.*\/>/g;
    const newLabel = currentLabel.match(imgTagRegex);

    if (newLabel !== null) {
      return currentLabel.replace(newLabel[0], '');
    }

    return currentLabel;
  };

  const deleteAnswer = (answerIndex: number) => {
    const newAnswersListComponents = [...answersListComponents];

    if (newAnswersListComponents.length === ORIGINAL_FORM_VALIDATION.ANSWER_MIN_SIZE) {
      handleFormValidationError(t('registration.originalForm.validationErrorModal.minAnswer'));
      return;
    }

    newAnswersListComponents.splice(answerIndex, 1);
    setAnswersListComponents(newAnswersListComponents);
    handleAnswerChange(newAnswersListComponents);
  };

  const updateAnswerLabelWithoutImg = (answerIndex: number, answerLabel: string) => {
    let currentLabel = answerLabel;

    const imgTagRegex = /<img.*\/>/g;
    const newLabel = currentLabel.match(imgTagRegex);

    if (newLabel !== null) {
      currentLabel = currentLabel.replace(newLabel[0], '');
    }

    const newAnswersListComponents = answersListComponents.map((answerItem, itemIdx) => {
      if (itemIdx === answerIndex) {
        return {
          ...answerItem,
          label: currentLabel,
        };
      }

      return answerItem;
    });

    setAnswersListComponents(newAnswersListComponents);
    handleAnswerChange(newAnswersListComponents);
  };

  const answerImgPreview = isImgPreviewForAnswer(item.label);

  useEffect(() => {
    if (!answersListComponents[answerIdx].isOtherAnswer) {
      setNodeRef(myRef.current);
    }
  }, [answersListComponents]);

  return (
    <>
      <ContentBox
        key={`${questionIdx}-${totalQuestions}-${answerIdx}-contentbox`}
        data-testid={`${answerIdx}-sa-answer-component-draggable`}
        style={style}
        ref={myRef}
      >
        <AnswerComponent
          key={`${questionIdx}-${totalQuestions}-${answerIdx}-component`}
          data-testid={`${answerIdx}-sa-answer-component`}
        >
          {item.isOtherAnswer ? (
            <div style={{ marginRight: '17px' }} />
          ) : (
            <div {...attributes} {...listeners}>
              <DragIconWrapper
                data-testid={`${questionIdx}-${answerIdx}-DragIndicatorOutlinedIcon`}
              />
            </div>
          )}

          <Typography
            variant="body3"
            sx={{
              color: PALETTE.text.main,
              ...(isValidate && !item.isValid && { color: PALETTE.error.main }),
            }}
          >
            {`${answerIdx + 1}.`}
          </Typography>

          {item.isOtherAnswer ? (
            <StyledDisabledTextDiv
              style={{
                width: singleAnswerConfig.gotoRequired ? '720px' : '100%',
              }}
            >
              <StyledDisabledText data-testid="other-sa-answer-label">
                {item.label}
              </StyledDisabledText>
            </StyledDisabledTextDiv>
          ) : (
            <AnswerInput>
              <InputValidationErrorTooltip
                error={showInputError(isValidate, item.isValid)}
                message={t(
                  getOriginalFormsValidationMsg(
                    getAnswerLabel(item.label),
                    ORIGINAL_FORM_VALIDATION.ANSWER_LABEL_MAX_SIZE
                  ),
                  {
                    maxLength: ORIGINAL_FORM_VALIDATION.ANSWER_LABEL_MAX_SIZE.toLocaleString(
                      LOCALE_STRING.ja
                    ),
                  }
                )}
              >
                <OutlinedInput
                  sx={{
                    margin: '-15px 5px -15px 10px',
                    color: PALETTE.text.main,
                    fontSize: 14,
                    padding: '10px',
                    background: PALETTE.white,
                    width: singleAnswerConfig.gotoRequired ? '310px' : '100%',
                    height: '40px',
                  }}
                  error={showInputError(isValidate, item.isValid)}
                  placeholder={t('registration.originalForm.manual.saComponent.placeholder')}
                  endAdornment={
                    !isPullDown && (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={`sa-upload-image-answer-${answerIdx}`}
                          onClick={() => {
                            setShowImageUploadModal(true);
                            setImageUploadAnswerIdx(answerIdx);
                          }}
                        >
                          <CropOriginalOutlinedIcon
                            sx={{
                              color: '#425F77',
                              fontSize: '20px',
                            }}
                          />
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    handleOnChangeComponent(event, answerIdx, 'label', false, answerImgPreview);
                  }}
                  onBlur={(event: FocusEvent<HTMLInputElement>) => {
                    handleOnChangeComponent(event, answerIdx, 'label', true, answerImgPreview);
                  }}
                  key={`${answerIdx}-label`}
                  value={getAnswerLabel(item.label)}
                  inputProps={{
                    'data-testid': `${answerIdx}-sa-answer-label`,
                  }}
                />
              </InputValidationErrorTooltip>
            </AnswerInput>
          )}

          {singleAnswerConfig.gotoRequired && (
            <FormControl size="small" sx={{ width: '100%' }}>
              <InputValidationErrorTooltip
                error={showInputError(isValidate, item.isGotoValid)}
                message={t('registration.originalForm.manual.inputBox.validationError')}
              >
                <Select
                  sx={{
                    color: PALETTE.text.main,
                    fontSize: '14px',
                    background: '#FFFFFF',
                    height: '40px',
                    margin: '-15px 5px -15px 10px',
                    marginLeft: '20px',
                    width: '290px',
                  }}
                  displayEmpty
                  id="gotoSelect"
                  value={
                    questionIds.some((qId) => qId.questionId === item.goto) || item.goto === 'END'
                      ? item.goto
                      : ''
                  }
                  onChange={(event) => {
                    handleOnChangeComponent(event, answerIdx, 'goto', true, answerImgPreview);
                  }}
                  data-testid={`${answerIdx}-answer-go-to-select`}
                  error={showInputError(isValidate, item.isGotoValid)}
                  key={`${answerIdx}-answer-go-to-select`}
                >
                  {getGotoOptions(answerIdx)}
                </Select>
              </InputValidationErrorTooltip>
            </FormControl>
          )}

          <IconButton
            sx={{ marginLeft: '15px' }}
            aria-label={`${answerIdx}-sa-delete-answer`}
            onClick={(event) => {
              event.stopPropagation();
              deleteAnswer(answerIdx);
            }}
          >
            <CloseOutlinedIcon />
          </IconButton>
        </AnswerComponent>

        {answerImgPreview && (
          <ImgPreviewContainer data-testid="answer-image-preview">
            <StyledImg src={answerImgPreview.props.src} alt={answerImgPreview.props.alt} />
            <IconButton
              onClick={() => updateAnswerLabelWithoutImg(answerIdx, item.label)}
              data-testid="deleteAnswerImg"
            >
              <CloseOutlinedIcon style={{ fontWeight: 'bold', fontSize: '20px' }} />
            </IconButton>
          </ImgPreviewContainer>
        )}
      </ContentBox>

      {showImageUploadModal && (
        <ImageUploadModal
          isOpen={showImageUploadModal}
          handleClose={() => {
            setShowImageUploadModal(false);
          }}
          handleSetting={handleImageUploadSetting}
        />
      )}
    </>
  );
};

export default SortableItem;
